import {faTriangleExclamation} from '@fortawesome/pro-regular-svg-icons';
import {faCircleInfo} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useQueryClient} from '@tanstack/react-query';
import {Button, Divider, Flex, Tooltip} from 'antd';
import {useCallback, useState} from 'react';
import {ExtractRouteParams, generatePath, useParams} from 'react-router';

import {appRoute} from '../../../../app-route';
import {AlertFallback} from '../../../../component/alert-fallback/alert-fallback';
import {Meta} from '../../../../component/meta/meta';
import {BreadCrumbs} from '../../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../../layout/page/page';
import {MainPageContainer} from '../../../../layout/page-card/main-page-container';
import {PageCard} from '../../../../layout/page-card/page-card';
import {PageHeader} from '../../../../layout/page-header/page-header';
import {Spinner} from '../../../../layout/spinner/spinner';
import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {Locale} from '../../../../provider/locale/localization';
import {useModal} from '../../../../provider/modal/modal-hook';
import {useReviewItem} from '../../../../service/reviews/reviews';
import {useReviewsAbuseReason} from '../../../../service/reviews/reviews-abuse-hook';
import {useReviewsCatalogList} from '../../../../service/reviews/reviews-catalogs';
import {
    ReviewsGmbCclStatusEnum,
    useReviewsGmbCclStatuses,
} from '../../../../service/reviews/reviews-gmb-confirmation-hook';
import {useToggleReviewVisibilityMutation} from '../../../../service/reviews/reviews-hide';
import {useReviewLogsList} from '../../../../service/reviews/reviews-logs';
import {NoteFormType, useNotesMutation} from '../../../../service/reviews/reviews-notes';
import {useReviewsSuggestions} from '../../../../service/reviews/reviews-suggestions';
import {ReviewDataType} from '../../../../service/reviews/reviews-type';
import {ProvidersIdsEnum} from '../../../../util/type';
import {useUrl} from '../../../../util/url-hook/url-hook';
import {useReviewActivityHook} from '../review-activity-provider/review-activity-provider';
import {ReviewAbusePopover} from '../review-list-page-content/review-list/review/reply-form/abuse-tag/review-abuse-popover';
import {NewAbuseModal} from '../review-list-page-content/review-list/review/reply-form/new-abuse-modal/new-abuse-modal';
import {ReviewHistoryModal} from '../review-list-page-content/review-list/review/review-header/history-modal/history-modal';
import {NoteModal} from '../review-list-page-content/review-list/review/review-header/note-modal/note-modal';
import {ResponsibleUserModal} from '../review-list-page-content/review-list/review/review-header/responsible-user-modal/responsible-user-modal';
import {useReviewMenu} from '../review-list-page-content/review-list/review/review-menu-hook/review-menu-hook';
import {ReviewDataWithCatalogType} from '../review-list-page-content/review-list/review-list-type';
import {ReviewStatus} from '../review-status/review-status';
import {isReviewAbuseAvailableWhenVisible} from '../reviews-helper';
import {useReviewStateHook} from '../reviews-state/reviews-state-provider';
import {TagList} from '../tag-list/tag-list';

import {ReviewWrapper} from './review-wrapper/review-wrapper';
import * as styles from './new-review-item-page-content.scss';

// eslint-disable-next-line complexity, max-statements
export function NewReviewItemPageContent(): JSX.Element | null {
    const {data: reviewsCatalogList, isFetching: reviewsCatalogListIsInProgress} = useReviewsCatalogList();

    const [isOpenNoteModal, setIsOpenNoteModal] = useState<boolean>(false);
    const [isOpenHistoryModal, setIsOpenHistoryModal] = useState<boolean>(false);
    const [isOpenResponsibleUserModal, setIsOpenResponsibleUserModal] = useState<boolean>(false);
    const [isOpenAbuseModal, setIsOpenAbuseModal] = useState<boolean>(false);

    const {getLocalizedString} = useLocale();
    const {id} = useParams<ExtractRouteParams<typeof appRoute.reviewsManagementReviewItem.path, string>>();
    const reviewId = Number(id);
    const {companyName} = useDomainConfig();
    const queryClient = useQueryClient();
    const {pushUrl} = useUrl();

    const {isHistoryVisible} = useReviewActivityHook();
    const {googleMapsReviewIds, refreshReview, refreshSuggestions} = useReviewStateHook();
    const {refetch: loadLogs} = useReviewLogsList(Number(id), isHistoryVisible);
    const {data: reviewsAbuseReason} = useReviewsAbuseReason([reviewId]);

    const {data: review, isLoading, error, refetch: refreshReviewItem, queryKey} = useReviewItem(reviewId);
    const {data: resultSuggestions} = useReviewsSuggestions([reviewId]);

    const catalog = reviewsCatalogList?.find((item) => item.id === review?.catalogId);

    const suggestion = resultSuggestions?.results?.[0];

    const {mutateAsync, isLoading: noteMutationLoading} = useNotesMutation();

    const {data: reviewGmbStatuses} = useReviewsGmbCclStatuses(googleMapsReviewIds);
    const {modal} = useModal();
    const isNeedConfirmationStatus =
        reviewGmbStatuses &&
        (reviewGmbStatuses[reviewId] === ReviewsGmbCclStatusEnum.WaitVerify ||
            reviewGmbStatuses[reviewId] === ReviewsGmbCclStatusEnum.NotVerified);

    const reviewAbuseReason = (reviewsAbuseReason || []).find((reviewReason) => reviewReason.id === reviewId) || null;

    const isHiddenReview = Boolean(review?.isHidden);
    const isAbuseVisibleWhenNotHidden = review ? isReviewAbuseAvailableWhenVisible(review) : false;

    const isShowExternalOptions = Boolean(catalog?.is_external_reviews) && !review?.deletedAt;

    const patchReviewCacheForVisibility = useCallback(
        (_id: number, partial: Partial<ReviewDataType>) => {
            queryClient.setQueryData([queryKey], (oldData?: ReviewDataType) => {
                if (!oldData) {
                    return oldData;
                }

                return {
                    ...oldData,
                    ...partial,
                };
            });
        },
        [queryClient, queryKey]
    );

    const {mutateAsync: setReviewVisibility} = useToggleReviewVisibilityMutation({
        updateReview: patchReviewCacheForVisibility,
    });

    const menuItems = useReviewMenu({
        catalogId: review?.catalogId,
        reviewId,
        isShowOpenNewTabButton: false,
        isShowExternalOptions,
        text: review?.comment || '',
        setIsOpenNoteModal,
        setIsOpenHistoryModal,
        setIsOpenResponsibleUserModal,
        ableToChangeResponsibleUser: Boolean(review?.ableToChangeResponsibleUser),
        onSubmitFinish: refreshReviewItem,
        isReviewDeleted: Boolean(review?.deletedAt),
        isHidden: Boolean(review?.isHidden),
        onSetHidden: async (nextHidden) => {
            await setReviewVisibility({reviewId, isHidden: nextHidden});
        },
    });

    const updateReview = useCallback(
        (partialReviewData: Partial<ReviewDataType>) => {
            queryClient.setQueryData([queryKey], (oldData?: ReviewDataType) => {
                if (!oldData) {
                    return oldData;
                }

                return {
                    ...oldData,
                    ...partialReviewData,
                };
            });
        },
        [queryClient, queryKey]
    );

    async function onSubmit(formValues: NoteFormType) {
        return mutateAsync(
            {text: (formValues.text ?? '').trim(), reviewId},
            {
                onSuccess: async (data) => {
                    await updateReview({notes: [data, ...(review?.notes ?? [])]});

                    setIsOpenNoteModal(false);
                },
            }
        );
    }

    const handleClick = useCallback(() => {
        if (isNeedConfirmationStatus) {
            modal.confirm({
                icon: <FontAwesomeIcon className={styles.ReviewPage_modalIcon} icon={faCircleInfo} size="xl" />,
                maskClosable: true,
                title: <Locale stringKey="REVIEW__ANSWER__CONFIRMATION_MODAL__TITLE" />,
                content: <Locale stringKey="REVIEW__ANSWER__CONFIRMATION_MODAL__CONTENT" valueMap={{companyName}} />,
                okText: <Locale stringKey="POPUP__BUTTON__CONFIRM" />,
                cancelText: <Locale stringKey="BUTTON__CANCEL" />,
                onOk: () => {
                    pushUrl(generatePath(appRoute.sourcesAccountSyncGoogleConfirmations.path));
                },
            });
        } else {
            setIsOpenAbuseModal(true);
        }
    }, [isNeedConfirmationStatus, modal, companyName, pushUrl]);

    const reviewWithCatalog: ReviewDataWithCatalogType | null = review ? {...review, catalog: catalog || null} : null;

    return (
        <Page isTopFilterHidden renderContainer={(children) => <MainPageContainer>{children}</MainPageContainer>}>
            <PageCard>
                <Meta title={getLocalizedString('REVIEW_ITEM_PAGE__TITLE')} />
                <BreadCrumbs
                    list={[
                        {
                            path: appRoute.reviewsManagementReviews.path,
                            titleLangKey: 'CATEGORY_NAME__REVIEWS',
                        },
                        {
                            path: generatePath(appRoute.reviewsManagementReviewItem.path, {id}),
                            titleLangKey: 'REVIEW_ITEM_PAGE__TITLE',
                        },
                    ]}
                />
                <PageHeader className={styles.ReviewPage_pageHeader}>
                    <Locale stringKey="REVIEW_ITEM_PAGE__TITLE" />
                </PageHeader>
            </PageCard>

            <div className={styles.ReviewPage_contentWrapper}>
                <Spinner isShow={isLoading} position="absolute" />

                {Boolean(error) && !isLoading && <AlertFallback />}

                {review && !error && (
                    <div className={styles.ReviewPage_content}>
                        {reviewWithCatalog && (
                            <ReviewWrapper
                                isCatalogLoading={reviewsCatalogListIsInProgress}
                                openHistoryModalHandler={() => setIsOpenHistoryModal(true)}
                                refreshReview={refreshReview}
                                refreshReviewItem={refreshReviewItem}
                                refreshSuggestions={refreshSuggestions}
                                review={reviewWithCatalog}
                                suggestion={suggestion}
                                updateReviewItem={updateReview}
                            />
                        )}
                        <div className={styles.ReviewPage_aside}>
                            <div className={styles.ReviewPage_asideContent}>
                                <div className={styles.ReviewPage_catalogLine}>
                                    <ReviewStatus
                                        className={styles.ReviewPage_status}
                                        onChange={loadLogs}
                                        processStatus={review.processStatus}
                                        reviewId={review.id}
                                        updateReview={(_reviewId, updateData) => updateReview(updateData)}
                                    />
                                </div>
                                <Flex align="start" className={styles.ReviewPage_actionBlock} vertical>
                                    {menuItems &&
                                        menuItems?.map((item) => (
                                            <Button
                                                icon={item.icon}
                                                key={item.key}
                                                // eslint-disable-next-line no-undefined
                                                onClick={item.onClick ?? undefined}
                                                style={{width: '100%', justifyContent: 'flex-start'}}
                                                type="text"
                                            >
                                                {item.label}
                                            </Button>
                                        ))}
                                </Flex>
                                <Divider className={styles.ReviewPage_divider} />
                                <Flex align="start" className={styles.ReviewPage_tagsBlock} vertical>
                                    <TagList
                                        deletedAt={review.deletedAt}
                                        refreshReview={(updatedReviewId) =>
                                            Promise.all([refreshReview(updatedReviewId), refreshReviewItem()])
                                        }
                                        responsibleUser={review.responsibleUser}
                                        reviewId={reviewId}
                                        tags={review.tags}
                                        updateReview={(_reviewId, updateData) => updateReview(updateData)}
                                    />
                                </Flex>
                                <Divider className={styles.ReviewPage_divider} />
                                <Flex className={styles.ReviewPage_abuseBlock}>
                                    <ReviewAbusePopover
                                        abuseLastDate={review?.abuseLastDate || null}
                                        catalogId={catalog?.id}
                                        reviewAbuseReason={reviewAbuseReason}
                                    />
                                    {!isHiddenReview && !review.abuseLastDate && review.ableToAbuse && (
                                        <Button onClick={handleClick} style={{width: '100%'}}>
                                            <Locale stringKey="REVIEWS__REVIEW_ABUSE__HINT" />
                                        </Button>
                                    )}
                                    {!isHiddenReview &&
                                        review.catalogId !== ProvidersIdsEnum.yandex &&
                                        review.abuseLastDate &&
                                        review.ableToAbuse && (
                                            <Tooltip
                                                placement="topRight"
                                                title={getLocalizedString('REVIEW__ABUSE__SEND_AGAIN')}
                                            >
                                                <Button className={styles.ReviewPage_abuseButton} onClick={handleClick}>
                                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                                </Button>
                                            </Tooltip>
                                        )}
                                    {isHiddenReview && !review.deletedAt && isAbuseVisibleWhenNotHidden && (
                                        <Tooltip title={<Locale stringKey="REVIEWS__REVIEW_HIDDEN_TOOLTIP" />}>
                                            <Button disabled style={{width: '100%'}}>
                                                <Locale stringKey="REVIEWS__REVIEW_ABUSE__HINT" />
                                            </Button>
                                        </Tooltip>
                                    )}
                                </Flex>
                                {isOpenNoteModal && (
                                    <NoteModal
                                        isLoading={noteMutationLoading}
                                        onCancel={setIsOpenNoteModal}
                                        onSubmit={onSubmit}
                                        reviewId={reviewId}
                                        title={<Locale stringKey="REVIEWS__NOTE_MODAL__TITLE" />}
                                    />
                                )}
                            </div>
                        </div>

                        {isOpenHistoryModal && (
                            <ReviewHistoryModal
                                onCancel={() => setIsOpenHistoryModal(false)}
                                replyTimeInCatalog={review?.replyTimeInCatalog}
                                replyTimeInRd={review?.replyTimeInRd}
                                reviewAuthor={review?.originUrl ? null : review?.author}
                                reviewCreatedInCatalog={review.createdInCatalog}
                                reviewId={reviewId}
                            />
                        )}

                        {isOpenResponsibleUserModal && reviewWithCatalog && (
                            <ResponsibleUserModal
                                initialSelectedUserId={review.responsibleUser?.id}
                                onCancel={() => {
                                    setIsOpenResponsibleUserModal(false);
                                }}
                                reviewData={reviewWithCatalog}
                                reviewId={reviewId}
                                updateReview={(_reviewId, updateData) => updateReview(updateData)}
                            />
                        )}
                        {isOpenAbuseModal && (
                            <NewAbuseModal
                                refreshReview={(_value) => refreshReviewItem()}
                                reviewId={reviewId}
                                setOpen={setIsOpenAbuseModal}
                            />
                        )}
                    </div>
                )}
            </div>
        </Page>
    );
}
